import * as React from "react";
import { usePlan } from "@authe/ui-core";
import { usePermissions } from "@authe/ui-core/permissions";
import { createScopedStorage } from "@authe/fe-commons/storage";
import { ENTITLEMENT } from "@authe/fe-commons/plans";
import { useActiveTeam } from "@/app/activeTeam";
import { useCurrentUserId, useStorageIdentity } from "@/app/storageIdentity";
import { useOrgMembers } from "@/features/team/useOrgMembers";
import { RESOURCES, ROUTES } from "@/config";
import { useLandingAccess } from "./useLandingAccess";
import { quickStartProfile, selectQuickStart } from "./quickStartModel";
import { useQuickStartWork } from "./quickStartWork";

export function useQuickStart() {
  const access = useLandingAccess();
  const { can } = usePermissions();
  const { plan, granted, settingCount } = usePlan();
  const userId = useCurrentUserId();
  const seatLimit = settingCount("seat_limit");
  const profile = quickStartProfile(plan?.id, plan?.metadata?.quick_start_profile, seatLimit);
  const canInvite = can(RESOURCES.team, "create");
  const canManageMembers = can(RESOURCES.team, "edit");
  const canCreateTeam = can(RESOURCES.teams, "create");
  const readMembers = Boolean(plan) && profile !== "individual" && access.destinations.has(ROUTES.team)
    && (canInvite || canManageMembers || canCreateTeam);
  // Shares the Team page's identity-scoped query cache. No polling on the home page.
  const members = useOrgMembers({ enabled: readMembers, refetchInterval: false });
  const team = useActiveTeam();
  const work = useQuickStartWork();
  const included = (capability: string) => granted === null || granted.has(capability);
  return selectQuickStart({
    profile, destinations: access.destinations, seatLimit, work,
    canInvite, canManageMembers, canCreateTeam, canCreateCase: access.canStartCase,
    canConfigureIntegrations: can(RESOURCES.integrations, "create") && included(ENTITLEMENT.CUSTOM_INTEGRATIONS),
    canConfigurePipelines: can(RESOURCES.pipelines, "write") && included(ENTITLEMENT.CUSTOM_PIPELINES),
    teams: team.isLoading || team.failed ? undefined : team.teams.length,
    members: !readMembers || members.isLoading || members.error ? undefined : {
      activeOthers: members.members.filter(m => m.id !== userId && !m.pending && m.status.toLowerCase() === "active" && m.roles.length > 0).length,
      pending: members.members.filter(m => m.pending).length,
      seatsUsed: members.members.filter(m => m.pending || m.status.toLowerCase() === "active").length,
    },
  });
}

/** A personal display preference, never a claim that onboarding was completed. */
export function useQuickStartMinimized() {
  const { app, customerId, orgId, tenantId, userId } = useStorageIdentity();
  const storage = React.useMemo(() => userId ? createScopedStorage({ app, customerId, orgId, tenantId, userId }) : null,
    [app, customerId, orgId, tenantId, userId]);
  const read = () => storage?.getAt<unknown>("quick-start:minimized", "user", false) === true;
  const [state, setState] = React.useState(() => ({ storage, minimized: read() }));
  const minimized = state.storage === storage ? state.minimized : read();
  if (state.storage !== storage) setState({ storage, minimized });
  const setMinimized = (next: boolean) => {
    storage?.set("quick-start:minimized", next, "user");
    setState({ storage, minimized: next });
  };
  return [minimized, setMinimized] as const;
}
